'use client';

import * as React from 'react';
import { ArrowLeft, MapPin, Users, Calendar } from 'lucide-react';

import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@workspace/ui/components/table';

import { type WeddingSampleDto, type WeddingSampleLineItem } from '~/data/budget-explorer/get-public-wedding-samples';

// Category display labels (matching dashboard)
const BUDGET_CATEGORY_LABELS: Record<string, string> = {
  VENUE: 'Venue',
  CATERING: 'Catering',
  PHOTOGRAPHY: 'Photography',
  VIDEOGRAPHY: 'Videography',
  FLORALS: 'Florals',
  MUSIC: 'Music & Entertainment',
  ATTIRE: 'Attire',
  BEAUTY: 'Beauty',
  STATIONERY: 'Stationery',
  TRANSPORTATION: 'Transportation',
  DECOR: 'Decor',
  RENTALS: 'Rentals',
  FAVORS: 'Favors & Gifts',
  OFFICIANT: 'Officiant',
  WEDDING_PLANNER: 'Wedding Planner',
  OTHER: 'Other',
};

// Category colors for visual distinction
const CATEGORY_COLORS: Record<string, string> = {
  VENUE: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  CATERING: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  PHOTOGRAPHY: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  VIDEOGRAPHY: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  FLORALS: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  MUSIC: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  ATTIRE: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  BEAUTY: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
  STATIONERY: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  TRANSPORTATION: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
  DECOR: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  RENTALS: 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300',
  FAVORS: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  OFFICIANT: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  WEDDING_PLANNER: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  OTHER: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

interface WeddingDetailViewProps {
  wedding: WeddingSampleDto;
  onBack: () => void;
}

// Group line items by category
interface CategoryGroup {
  category: string;
  label: string;
  items: WeddingSampleLineItem[];
  total: number;
  percentage: number;
}

function groupItemsByCategory(items: WeddingSampleLineItem[], totalSpend: number): CategoryGroup[] {
  const groups: Record<string, WeddingSampleLineItem[]> = {};
  
  for (const item of items) {
    if (!groups[item.category]) {
      groups[item.category] = [];
    }
    groups[item.category].push(item);
  }
  
  return Object.entries(groups)
    .map(([category, categoryItems]) => {
      const total = categoryItems.reduce((sum, item) => sum + item.amount, 0);
      return {
        category,
        label: BUDGET_CATEGORY_LABELS[category] || category,
        items: categoryItems.sort((a, b) => b.amount - a.amount),
        total,
        percentage: totalSpend > 0 ? Math.round((total / totalSpend) * 100) : 0,
      };
    })
    .sort((a, b) => b.total - a.total);
}

export function WeddingDetailView({ wedding, onBack }: WeddingDetailViewProps): React.JSX.Element {
  const categoryGroups = React.useMemo(
    () => groupItemsByCategory(wedding.lineItems, wedding.totalSpend),
    [wedding.lineItems, wedding.totalSpend]
  );

  // Calculate per-guest cost
  const perGuestCost = wedding.guestCount > 0 
    ? Math.round(wedding.totalSpend / wedding.guestCount) 
    : 0;

  return (
    <div className="space-y-3">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 h-7 text-xs">
          <ArrowLeft className="h-3 w-3" />
          Back to list
        </Button>
      </div>

      {/* Wedding Summary - Compact */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b">
        <div className="flex items-center gap-3">
          <span className="text-xl font-bold font-mono">
            ${wedding.totalSpend.toLocaleString()}
          </span>
          <span className="text-sm text-muted-foreground">Total Budget</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="gap-1 px-2 py-0.5 text-xs">
            <MapPin className="h-3 w-3" />
            {wedding.location}
          </Badge>
          <Badge variant="outline" className="gap-1 px-2 py-0.5 text-xs">
            <Users className="h-3 w-3" />
            {wedding.guestCount}
          </Badge>
          <Badge variant="outline" className="gap-1 px-2 py-0.5 text-xs">
            <Calendar className="h-3 w-3" />
            {wedding.weddingYear}
          </Badge>
          <Badge variant="outline" className="gap-1 px-2 py-0.5 text-xs">
            ${perGuestCost}/guest
          </Badge>
        </div>
      </div>

      {/* Compact Line Items Table */}
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-8 text-xs font-semibold w-[35%]">Item</TableHead>
            <TableHead className="h-8 text-xs font-semibold w-[25%]">Category</TableHead>
            <TableHead className="h-8 text-xs font-semibold text-right w-[20%]">Amount</TableHead>
            <TableHead className="h-8 text-xs font-semibold text-right w-[20%]">% of Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categoryGroups.map((group) => (
            <React.Fragment key={group.category}>
              {/* Category Header Row */}
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableCell colSpan={2} className="py-1.5 font-semibold text-xs">
                  <Badge 
                    variant="secondary" 
                    className={`${CATEGORY_COLORS[group.category] || CATEGORY_COLORS.OTHER} text-[10px] px-1.5 py-0`}
                  >
                    {group.label}
                  </Badge>
                  <span className="ml-2 text-muted-foreground font-normal">
                    ({group.items.length})
                  </span>
                </TableCell>
                <TableCell className="py-1.5 text-right font-mono font-semibold text-xs">
                  ${group.total.toLocaleString()}
                </TableCell>
                <TableCell className="py-1.5 text-right text-xs text-muted-foreground">
                  {group.percentage}%
                </TableCell>
              </TableRow>
              {/* Line Items */}
              {group.items.map((item) => (
                <TableRow key={item.id} className="hover:bg-accent/30">
                  <TableCell className="py-1 pl-4 text-xs">{item.itemName}</TableCell>
                  <TableCell className="py-1 text-xs text-muted-foreground">—</TableCell>
                  <TableCell className="py-1 text-right font-mono text-xs">
                    ${item.amount.toLocaleString()}
                  </TableCell>
                  <TableCell className="py-1 text-right text-xs text-muted-foreground">
                    {wedding.totalSpend > 0 ? ((item.amount / wedding.totalSpend) * 100).toFixed(1) : 0}%
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {/* Summary Footer - Compact */}
      <div className="flex justify-between items-center pt-2 border-t text-xs">
        <span className="text-muted-foreground">
          {wedding.lineItems.length} items • {categoryGroups.length} categories
        </span>
        <span className="font-mono font-semibold">
          Total: ${wedding.totalSpend.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

